import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { analyticsConsentGiven, setAnalyticsConsent } from '@/analytics/analytics';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

/**
 * Prywatność i dane — mock: prywatno_i_dane. Explains the local-first promise
 * in plain words and hosts the analytics consent toggle (moved out of Więcej).
 * Every claim here MUST stay true to the code: data stays on device, cloud
 * backup is an opt-in opaque blob the server never parses, transport is TLS.
 * No invented crypto badges (the mock's "AES-256" was dropped on purpose).
 */
export default function PrivacyScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  // Device-level analytics consent (works for guests too). OFF by default; the
  // toggle is the ONLY thing that turns collection on (see analytics.ts).
  const policyCloudBackup = useAppMetaStore((s) => s.policyCloudBackup);
  const setPolicyCloudBackup = useAppMetaStore((s) => s.setPolicyCloudBackup);
  const policyHealthConsent = useAppMetaStore((s) => s.policyHealthConsent);
  const setPolicyHealthConsent = useAppMetaStore((s) => s.setPolicyHealthConsent);
  const [analytics, setAnalytics] = useState(analyticsConsentGiven());
  const onToggleAnalytics = (value: boolean) => {
    setAnalytics(value);
    void setAnalyticsConsent(value);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('privacy.title')} />

        {/* Hero */}
        <View style={{ alignItems: 'center', gap: spacing.sm }}>
          <View
            style={{
              width: 88,
              height: 88,
              borderRadius: radius.full,
              backgroundColor: colors.primary,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="shield-outline" size={44} color="#FFFFFF" />
          </View>
          <AppText variant="headlineMd" style={{ textAlign: 'center' }}>
            {t('privacy.heroTitle')}
          </AppText>
          <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
            {t('privacy.heroSubtitle')}
          </AppText>
        </View>

        {/* Promise 1: local by default */}
        <Card style={{ flexDirection: 'row', gap: spacing.sm }}>
          <IconTile icon="phone-portrait-outline" bg={colors.primaryTile} fg={colors.navy} size={48} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="headlineSm">{t('privacy.localTitle')}</AppText>
            <AppText variant="bodyMd">{t('privacy.localBody')}</AppText>
          </View>
        </Card>

        {/* Promise 2: no monitoring — disarms the "Warta for You" objection
            (apps that watched driving style burned clients on insurer apps);
            agent feedback 2026-07-11 (wymagania-zanety). Every word stays true:
            no location, no sensors, contact signal only on explicit tap. */}
        <Card style={{ flexDirection: 'row', gap: spacing.sm }}>
          <IconTile icon="eye-off-outline" bg={colors.primaryTile} fg={colors.navy} size={48} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="headlineSm">{t('privacy.noTrackingTitle')}</AppText>
            <AppText variant="bodyMd">{t('privacy.noTrackingBody')}</AppText>
          </View>
        </Card>

        {/* Promise 3: anonymous */}
        <Card style={{ flexDirection: 'row', gap: spacing.sm }}>
          <IconTile icon="ellipse-outline" bg={colors.primaryTile} fg={colors.navy} size={48} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="headlineSm">{t('privacy.anonTitle')}</AppText>
            <AppText variant="bodyMd">{t('privacy.anonBody')}</AppText>
          </View>
        </Card>

        {/* Promise 3: opt-in cloud copy — stored, never analyzed. Tapping the card
            goes to login (cloud backup needs an account) — owner request. */}
        <Pressable accessibilityRole="button" onPress={() => router.push('/auth/login')}>
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <IconTile icon="cloud-upload-outline" bg={colors.primary} fg="#FFFFFF" size={48} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="headlineSm">{t('privacy.cloudTitle')}</AppText>
              <AppText variant="bodyMd">{t('privacy.cloudBody')}</AppText>
            </View>
            <Ionicons name="chevron-forward" size={18} color={colors.primary} />
          </Card>
        </Pressable>

        {/* Honest security note: TLS + AWS EU (no invented crypto claims) */}
        <View
          style={{
            alignItems: 'center',
            gap: spacing.xs,
            backgroundColor: colors.disabledBg,
            borderRadius: radius.card,
            padding: spacing.lg,
          }}
        >
          <Ionicons name="lock-closed-outline" size={28} color={colors.navy} />
          <AppText variant="labelMd" color={colors.navy} style={{ letterSpacing: 1.2, textAlign: 'center' }}>
            {t('privacy.securityNote')}
          </AppText>
        </View>

        {/* Analytics consent lives here now (privacy is its natural home) */}
        <Card style={{ gap: spacing.xs }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="stats-chart-outline" size={22} color={colors.navy} />
            <View style={{ flex: 1 }}>
              <AppText variant="bodyLg">{t('more.analyticsTitle')}</AppText>
            </View>
            <Switch
              value={analytics}
              onValueChange={onToggleAnalytics}
              accessibilityLabel={t('more.analyticsTitle')}
              trackColor={{ false: colors.outlineVariant, true: colors.primary }}
              thumbColor="#FFFFFF"
              ios_backgroundColor={colors.outlineVariant}
            />
          </View>
          <AppText variant="bodyMd" color={colors.outline}>
            {t('more.analyticsHint')}
          </AppText>
        </Card>

        {/* Policy-scan cloud backup (opt-in) — only for logged-in users */}
        {account && (
          <Card style={{ gap: spacing.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="cloud-upload-outline" size={22} color={colors.navy} />
              <View style={{ flex: 1 }}>
                <AppText variant="bodyLg">{t('privacy.policyBackupTitle')}</AppText>
              </View>
              <Switch
                value={policyCloudBackup}
                onValueChange={(v) => setPolicyCloudBackup(v)}
                accessibilityLabel={t('privacy.policyBackupTitle')}
                trackColor={{ false: colors.outlineVariant, true: colors.primary }}
                thumbColor="#FFFFFF"
                ios_backgroundColor={colors.outlineVariant}
              />
            </View>
            <AppText variant="bodyMd" color={colors.outline}>
              {t('privacy.policyBackupHint')}
            </AppText>
            {/* Separate art. 9 consent for life/health policy scans */}
            {policyCloudBackup && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs }}>
                <Ionicons name="medkit-outline" size={20} color={colors.navy} />
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {t('privacy.policyHealthConsent')}
                </AppText>
                <Switch
                  value={policyHealthConsent}
                  onValueChange={(v) => setPolicyHealthConsent(v)}
                  accessibilityLabel={t('privacy.policyHealthConsent')}
                  trackColor={{ false: colors.outlineVariant, true: colors.primary }}
                  thumbColor="#FFFFFF"
                  ios_backgroundColor={colors.outlineVariant}
                />
              </View>
            )}
          </Card>
        )}

        {/* Guest -> the cloud copy needs an account first */}
        {!account && (
          <PrimaryButton label={t('privacy.loginForCloud')} onPress={() => router.push('/auth/login')} />
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
